import { Dispatch, FC, SetStateAction } from 'react';
import { Box, Fade, VStack } from '@chakra-ui/react';

import flyingBills from 'assets/brand/flying-bills@2x.png';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import DecorativeImage from 'components/DecorativeImage';
import Upload, { FILE_TYPE } from 'components/Upload';
import { UploadedFileInfo } from 'components/Upload/types';
import { getUploadBulkTransferFunction } from 'features/Storage/upload';

import locales from './locales';

interface FileUploadProps {
  onSuccessfulUpload: (uploadedFile: UploadedFileInfo) => Promise<void>;
  isSuccess: boolean;
  isLoading: boolean;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
}

const FileUpload: FC<FileUploadProps> = ({
  isLoading,
  isSuccess,
  onSuccessfulUpload,
  setIsUploading,
}) => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is undefined in BulkTransfers > FileUpload',
    );
  }

  const uploadText = isLoading ? (
    <Text> {locales.uploading} </Text>
  ) : (
    <Button
      as="div"
      icon="plus"
      iconPosition="right"
      variant="inline-secondary"
    >
      {locales.uploadMessage}
    </Button>
  );

  return (
    <Box flex={1}>
      <Upload
        errorMessage={locales.error}
        fileTypes={FILE_TYPE.BULK_TRANSFERS_ALLOWED_DOCUMENTS}
        onSuccessfulUpload={onSuccessfulUpload}
        onUploadStart={() => setIsUploading(true)}
        uploadFile={getUploadBulkTransferFunction(companyProfileId)}
        withLoader={false}
      >
        <VStack justify="center" padding="space-24" textAlign="center">
          <Fade in={isSuccess} unmountOnExit>
            <VStack py="space-24" spacing="space-8">
              <DecorativeImage heightPx={90} src={flyingBills} widthPx={90} />
              <Text variant="primary">{locales.uploadSuccess}</Text>
            </VStack>
          </Fade>

          {!isSuccess ? (
            <VStack>
              <SquircleIcon
                bgColor="white"
                marginBottom="space-16"
                name="file"
              />

              {uploadText}
              <Fade in={!isLoading}>
                <Text>{locales.acceptedFormat}</Text>
              </Fade>
            </VStack>
          ) : null}
        </VStack>
      </Upload>
    </Box>
  );
};

export default FileUpload;
